import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Spinner } from 'react-bootstrap';
import { MdClose } from 'react-icons/md';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import RadioButton from 'components/inputs/RadioButton';
import Select from 'components/inputs/Select';
import { UPDATE_STATUS_CODE } from 'components/common/constant';
import { GET_FLEET_RESERVATION, RESCHEDULE_FLEET_RESERVATION, CANCEL_FLEET_RESERVATION } from 'actions/fleetReservation';
import {
  SCOPES,
  BUFFER_MINUTES,
  formatDate,
  formatTime,
  formatWindow,
  blockStartOf,
  editableDates,
  connectorLabel,
  buildScopePayload,
  DateField,
  TimeField,
} from './fleetReservationUtils';

const emptyForm = { scope: 'THIS_OCCURRENCE', occurrenceDate: '', newDate: '', timeFrom: '', timeTo: '', reason: '' };

const FleetReservationActionModal = ({ action, reservationCode, occurrenceDate, rowId, onClose, onDone }) => {
  const dispatch = useDispatch();
  const { detail, isSaving } = useSelector((state) => state.fleetReservation);
  const [form, setForm] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const isCancel = action === 'cancel';
  const isLoaded = _.get(detail, 'reservationCode') === reservationCode;
  const currentRow = isLoaded ? _.find(detail.occurrences, { id: rowId }) : null;
  const dates = useMemo(() => (isLoaded ? editableDates(detail, action, currentRow) : []), [detail, isLoaded, action, currentRow]);

  useEffect(() => {
    dispatch({ type: GET_FLEET_RESERVATION.REQUEST, payload: reservationCode });
  }, [reservationCode]);

  useEffect(() => {
    if (!isLoaded || form) return;
    setForm({
      ...emptyForm,
      occurrenceDate: dates.includes(occurrenceDate) ? occurrenceDate : dates[0] || '',
      timeFrom: formatTime(_.get(currentRow, 'bookedStartTime') || detail.bookedStartTime),
      timeTo: formatTime(_.get(currentRow, 'bookedEndTime') || detail.bookedEndTime),
    });
  }, [isLoaded]);

  if (!form) {
    return (
      <Modal show centered size="lg" onHide={onClose}>
        <div className="fr-page p-5 text-center">
          <Spinner animation="border" size="sm" />
        </div>
      </Modal>
    );
  }

  const needsDate = form.scope !== 'ENTIRE_RESERVATION';
  const formErrors = {
    occurrenceDate:
      needsDate && !form.occurrenceDate
        ? 'Pick the booked day'
        : needsDate && !dates.includes(form.occurrenceDate)
          ? `Nothing ${isCancel ? 'active' : 'that can still be changed'} is booked on this day`
          : '',
    time: !isCancel && (!form.timeFrom || !form.timeTo || form.timeTo <= form.timeFrom) ? 'End time must be after start time' : '',
    reason: !form.reason.trim() ? (isCancel ? 'Reason for cancellation is required' : 'Reason for change is required') : '',
  };

  const setFormField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setScope = (scope) =>
    setForm((prev) => ({
      ...prev,
      scope,
      occurrenceDate: scope === 'ENTIRE_RESERVATION' ? '' : prev.occurrenceDate || dates[0] || '',
      newDate: scope === 'THIS_OCCURRENCE' ? prev.newDate : '',
    }));
  const effectiveBlock = form.timeFrom && form.timeTo ? formatWindow(blockStartOf(form.timeFrom), form.timeTo) : '';

  const handleSubmit = () => {
    setSubmitted(true);
    if (_.some(formErrors)) return;
    const reason = form.reason.trim();
    const payload = isCancel
      ? { ...buildScopePayload(form), rowId, cancelReason: reason }
      : {
          ...buildScopePayload(form),
          rowId,
          updates: {
            ...(form.scope === 'THIS_OCCURRENCE' && form.newDate && { newDate: form.newDate }),
            timeFrom: form.timeFrom,
            timeTo: form.timeTo,
          },
          reason,
        };
    dispatch({
      type: isCancel ? CANCEL_FLEET_RESERVATION.REQUEST : RESCHEDULE_FLEET_RESERVATION.REQUEST,
      payload: { reservationCode, ...payload },
      cb: (res) => {
        if (_.get(res, 'status') !== UPDATE_STATUS_CODE) return;
        toast.success(isCancel ? 'Reservation cancelled' : 'Reservation rescheduled');
        onDone();
      },
    });
  };

  return (
    <Modal show centered size="lg" onHide={onClose}>
      <div className="customer-form-page__main fr-page">
        <Card>
          <div className="customer-form-header__block">
            <div className="customer-header-name">
              <h5>
                {isCancel ? 'Cancel Reservation' : 'Reschedule Reservation'} — <span className="fr-code">{reservationCode}</span>
              </h5>
              {currentRow && (
                <div className="fr-muted small">
                  Only charger {currentRow.chargerOcppId}
                  {_.isNil(currentRow.connectorId) ? '' : `, connector ${connectorLabel(currentRow.connectorId)}`} will be{' '}
                  {isCancel ? 'cancelled' : 'changed'}
                </div>
              )}
            </div>
            <div className="close___btn">
              <MdClose size={30} color="#BE210B" onClick={onClose} />
            </div>
          </div>

          <div className="fr-modal-step">
            <div className="fr-modal-step__title">1. Which days do you want to {isCancel ? 'cancel' : 'change'}?</div>
            <div className="d-flex flex-column gap-2">
              {SCOPES.map((option) => (
                <RadioButton
                  key={option.value}
                  id={`fr-scope-${option.value}`}
                  name="scope"
                  checked={form.scope === option.value}
                  onChange={() => setScope(option.value)}
                >
                  {option.label}
                </RadioButton>
              ))}
            </div>
            <div className="fr-helper">
              e.g.{' '}
              {_.find(SCOPES, { value: form.scope }).hint(
                form.occurrenceDate ? formatDate(form.occurrenceDate, 'D MMM YYYY') : 'the day you pick',
                isCancel ? 'cancelled' : 'changed'
              )}
            </div>
          </div>

          <div className="fr-modal-step">
            <div className="fr-modal-step__title">2. Pick the booked day</div>
            <Select
              options={needsDate ? dates.map((date) => ({ label: formatDate(date, 'ddd, D MMM YYYY'), value: date })) : []}
              value={form.occurrenceDate}
              placeholder={needsDate ? 'Select a booked day' : 'All upcoming days'}
              isDisabled={!needsDate}
              disableSort
              onChange={(value) => setFormField('occurrenceDate', value || '')}
            />
            {needsDate && submitted && formErrors.occurrenceDate && <div className="text-danger small mt-1">{formErrors.occurrenceDate}</div>}
            <div className="fr-helper">
              {needsDate
                ? `${dates.length} booked day${dates.length === 1 ? '' : 's'} can still be picked.`
                : 'Not needed — all upcoming days are included.'}
            </div>
          </div>

          {isCancel ? (
            <div className="fr-modal-step">
              <div className="fr-modal-step__title">3. Why is it being cancelled? *</div>
              <input className="form-control fr-input" maxLength={200} value={form.reason} onChange={(e) => setFormField('reason', e.target.value)} />
              {submitted && formErrors.reason && <div className="text-danger small mt-1">{formErrors.reason}</div>}
            </div>
          ) : (
            <>
              <div className="fr-modal-step">
                <div className="fr-modal-step__title">
                  3. Move to a different day? <span className="fr-muted fw-normal">(optional)</span>
                </div>
                <DateField
                  value={form.newDate}
                  min={moment().format('YYYY-MM-DD')}
                  disabled={form.scope !== 'THIS_OCCURRENCE'}
                  onChange={(value) => setFormField('newDate', value)}
                />
                <div className="fr-helper">
                  {form.scope === 'THIS_OCCURRENCE'
                    ? 'Leave empty to keep the same day and only change the time.'
                    : 'Only possible when changing just one day.'}
                </div>
              </div>

              <div className="fr-modal-step">
                <div className="fr-modal-step__title">4. New time</div>
                <div className="row g-3">
                  <div className="col-6">
                    <div className="fr-label">From</div>
                    <TimeField value={form.timeFrom} onChange={(value) => setFormField('timeFrom', value)} />
                  </div>
                  <div className="col-6">
                    <div className="fr-label">To</div>
                    <TimeField value={form.timeTo} onChange={(value) => setFormField('timeTo', value)} />
                  </div>
                </div>
                {effectiveBlock && (
                  <div className="fr-helper">
                    The charger is held {BUFFER_MINUTES} minutes early for the fleet, so the public can&apos;t use it from{' '}
                    <b className="fr-blocked">{effectiveBlock}</b>
                  </div>
                )}
                {submitted && formErrors.time && <div className="text-danger small mt-1">{formErrors.time}</div>}
              </div>

              <div className="fr-modal-step">
                <div className="fr-modal-step__title">5. Why is it being changed? *</div>
                <input
                  className="form-control fr-input"
                  maxLength={200}
                  value={form.reason}
                  onChange={(e) => setFormField('reason', e.target.value)}
                />
                {submitted && formErrors.reason && <div className="text-danger small mt-1">{formErrors.reason}</div>}
              </div>
            </>
          )}

          <div className="d-flex flex-wrap justify-content-end gap-2 mt-4">
            <button type="button" className="btn fr-btn-outline" onClick={onClose}>
              {isCancel ? 'Keep Reservation' : 'Cancel'}
            </button>
            <Button disabled={isSaving || !dates.length} onClick={handleSubmit}>
              {isCancel ? 'Confirm Cancellation' : 'Confirm Changes'}
            </Button>
          </div>
        </Card>
      </div>
    </Modal>
  );
};

FleetReservationActionModal.propTypes = {
  action: PropTypes.oneOf(['reschedule', 'cancel']).isRequired,
  reservationCode: PropTypes.string.isRequired,
  occurrenceDate: PropTypes.string,
  rowId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

export default FleetReservationActionModal;
